import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, filter, finalize, interval, switchMap, take, tap } from 'rxjs';
import { AstralTab } from '../../../core/component/astral-components/astral-tabs/astral-tabs.component';
import { SortEvent, State } from '../../../core/component/list/list-view-header/list-view-header.interface';
import { PagingEvent } from '../../../core/component/list/paging/paging.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { IdentityLog, ProtectedIdentityContact, ProtectedIdentityContactForm } from '../../model/protected-identity';
import { ProtectedIdentityService } from '../../service/data/protected-identity.service';
import { IdNumberValidatorService } from '../../../common/service/id-number-validator-service';

@Component({
  selector: 'app-protected-identity-sidebar',
  templateUrl: './protected-identity-sidebar.component.html',
  styleUrls: ['./protected-identity-sidebar.component.scss'],
  providers: [IdNumberValidatorService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProtectedIdentitySidebarComponent implements OnInit, OnDestroy {
  @Input() contactId: string;
  @Output() close = new EventEmitter<void>();

  readonly tabs: AstralTab[] = [
    { id: 'personal-data', labelKey: 'PROTECTED_IDENTITY.PERSONAL_DATA' },
    { id: 'event-log', labelKey: 'PROTECTED_IDENTITY.EVENT_LOG' },
  ];

  selectedTabId = 'personal-data';
  form: FormGroup;
  data: ProtectedIdentityContact;
  logs: IdentityLog[] = [];
  logColumns: ColumnDefinition<IdentityLog>[];
  logPage = 1;
  logPageSize = 50;
  logTotalCount = 0;
  logsLoaded = false;
  isLoading = true;
  isConfirmingDeactivation = false;
  isExtendingAccess = false;
  secondsRemaining = 0;

  private readonly subscriptions = new Subscription();
  private timerSubscription?: Subscription;
  private logSubscription?: Subscription;
  private logOrderBy?: string;
  private logDescending = true;
  private accessExpiresAt = 0;
  private isCheckingAccess = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly controls: FormControlExFactory,
    columnFactory: ColumnDefinitionFactory,
    private readonly service: ProtectedIdentityService,
    private readonly messageService: MessageService,
    private readonly idNumberValidator: IdNumberValidatorService
  ) {
    this.logColumns = [
      columnFactory.create('PROTECTED_IDENTITY.LOG_USER', 'userName', {
        visibility: ColumnVisibility.mandatory,
        serverSideSortBinding: 'UserName',
      }),
      columnFactory.createTranslation('PROTECTED_IDENTITY.LOG_EVENT', 'eventTranslation', {
        visibility: ColumnVisibility.mandatory,
        serverSideSortBinding: 'EventType',
      }),
      columnFactory.createDateTime('PROTECTED_IDENTITY.LOG_TIME', 'createdAt', {
        visibility: ColumnVisibility.mandatory,
        serverSideSortBinding: 'CreatedAt',
      }),
    ];
  }

  get canActivate(): boolean {
    return this.data?.canActivate === true;
  }

  get formattedTime(): string {
    const minutes = Math.floor(this.secondsRemaining / 60);
    const seconds = this.secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.service
        .get(this.contactId)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: data => this.applyData(data),
          error: () => this.closeForLostAccess(),
        })
    );
  }

  onTabChange(tabId: string): void {
    this.selectedTabId = tabId;
    if (tabId === 'event-log' && !this.logsLoaded) {
      this.loadLogs();
    }
  }

  activate(): void {
    if (this.form.invalid || this.isLoading) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.subscriptions.add(
      this.service
        .activate(this.contactId, this.formValue())
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: data => {
            this.applyData(data);
            this.logsLoaded = false;
            this.messageService.showSuccess('PROTECTED_IDENTITY.ACTIVATED_SUCCESS');
          },
          error: () => {},
        })
    );
  }

  save(): void {
    if (this.form.invalid || this.isLoading) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.subscriptions.add(
      this.service
        .update(this.contactId, this.formValue())
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: data => {
            this.applyData(data);
            this.logsLoaded = false;
            this.messageService.showSuccess('PROTECTED_IDENTITY.UPDATED_SUCCESS');
          },
          error: () => {},
        })
    );
  }

  deactivate(): void {
    if (this.isLoading || this.isConfirmingDeactivation) return;

    this.isConfirmingDeactivation = true;
    this.subscriptions.add(
      this.messageService
        .showDialog('PROTECTED_IDENTITY.DEACTIVATE_CONFIRMATION', {
          okText: 'PROTECTED_IDENTITY.DEACTIVATE',
          cancelText: 'CANCEL_BTN',
          status: 'danger',
        })
        .pipe(
          take(1),
          filter(confirmed => confirmed),
          tap(() => (this.isLoading = true)),
          switchMap(() => this.service.deactivate(this.contactId)),
          finalize(() => {
            this.isLoading = false;
            this.isConfirmingDeactivation = false;
          })
        )
        .subscribe({
          next: () => {
            this.applyData({
              canActivate: true,
              name: '',
              civicNumber: '',
              sessionSecondsRemaining: this.secondsRemaining,
            });
            this.logsLoaded = false;
            this.messageService.showSuccess('PROTECTED_IDENTITY.DEACTIVATED_SUCCESS');
          },
          error: () => {},
        })
    );
  }

  extendAccess(): void {
    if (this.isLoading || this.isExtendingAccess || this.secondsRemaining === 0) return;

    this.isExtendingAccess = true;
    this.subscriptions.add(
      this.service
        .extendAccess()
        .pipe(finalize(() => (this.isExtendingAccess = false)))
        .subscribe({
          next: access => this.startTimer(access.sessionSecondsRemaining),
          error: () => this.closeForLostAccess(),
        })
    );
  }

  onLogPageChange(event: PagingEvent): void {
    if (this.logPage === event.pageNumber && this.logPageSize === event.itemsPerPage) return;

    this.logPage = event.pageNumber;
    this.logPageSize = event.itemsPerPage;
    this.loadLogs();
  }

  onLogSortChange(event: SortEvent): void {
    this.logOrderBy = event.state === State.DEFAULT ? undefined : event.column.serverSideSortBinding;
    this.logDescending = event.state === State.ORDERBYDESC;
    this.loadLogs();
  }

  ngOnDestroy(): void {
    this.dismissDeactivationDialog();
    this.clearSensitiveState();
    this.subscriptions.unsubscribe();
    this.stopTimer();
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.refreshAccessState();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') this.refreshAccessState();
  }

  private applyData(data: ProtectedIdentityContact): void {
    this.data = data;
    this.form = this.formBuilder.group({
      name: this.controls.createMandatory('PROTECTED_IDENTITY.NAME', data.name ?? ''),
      civicNumber: this.controls.createWithOptions('PROTECTED_IDENTITY.CIVIC_NUMBER', data.civicNumber ?? '', {
        required: true,
        validator: this.idNumberValidator.swedishSSN,
      }),
    });

    this.startTimer(data.sessionSecondsRemaining);
  }

  private formValue(): ProtectedIdentityContactForm {
    return {
      name: this.form.controls['name'].value,
      civicNumber: this.form.controls['civicNumber'].value,
    };
  }

  private loadLogs(): void {
    this.logsLoaded = true;
    this.logSubscription?.unsubscribe();
    this.logSubscription = this.service
      .getLogs(this.contactId, {
        page: this.logPage,
        pageSize: this.logPageSize,
        orderBy: this.logOrderBy,
        descending: this.logDescending,
      })
      .subscribe({
        next: result => {
          this.logs = result.items;
          this.logTotalCount = result.totalResults;
        },
        error: () => (this.logsLoaded = false),
      });
    this.subscriptions.add(this.logSubscription);
  }

  private startTimer(seconds: number): void {
    this.stopTimer();
    this.accessExpiresAt = Date.now() + Math.max(0, seconds) * 1000;
    this.updateTimer();
    if (this.secondsRemaining === 0) return;

    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateTimer();
    });
  }

  private updateTimer(): void {
    this.secondsRemaining = Math.max(0, Math.ceil((this.accessExpiresAt - Date.now()) / 1000));
    if (this.secondsRemaining > 0) return;

    this.stopTimer();
    this.dismissDeactivationDialog();
    this.messageService.showError('PROTECTED_IDENTITY.ACCESS_EXPIRED');
    this.closeForLostAccess();
  }

  private refreshAccessState(): void {
    if (this.isCheckingAccess) return;

    this.isCheckingAccess = true;
    this.subscriptions.add(
      this.service
        .getAccess(false)
        .pipe(finalize(() => (this.isCheckingAccess = false)))
        .subscribe({
          next: access => {
            if (!access.hasAccess) {
              this.closeForLostAccess();
              return;
            }
            this.startTimer(access.sessionSecondsRemaining);
          },
          error: () => this.closeForLostAccess(),
        })
    );
  }

  private closeForLostAccess(): void {
    this.clearSensitiveState();
    this.close.emit();
  }

  private clearSensitiveState(): void {
    this.form?.reset({ name: '', civicNumber: '' }, { emitEvent: false });
    this.logs = [];
    this.logTotalCount = 0;
    this.logsLoaded = false;
    this.dismissDeactivationDialog();
    this.stopTimer();
  }

  private stopTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;
    this.accessExpiresAt = 0;
    this.secondsRemaining = 0;
  }

  private dismissDeactivationDialog(): void {
    if (this.isConfirmingDeactivation) {
      this.messageService.dismissDialog();
    }
  }
}
